import { type FunctionComponent } from "react";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import moment, { type Moment } from "moment-timezone";

import { type IncidentReportIndividualSupportSessionRiskAssessment } from "components/CriticalIncident/interfaces";
import { FormControlWrapper } from "components/Shared";
import { TextBox, DateInput } from "components/Shared/Inputs";
import { ChooseYesNoOther } from "components/CriticalIncident/components/ChooseYesNoOther";
import { PriorityAlert } from "components/Shared/PriorityAlert";
import { useRiskAssessmentFieldActions } from "../../../RiskAssessments/hooks/useRiskAssessmentFieldActions";
import { RiskAssessmentFieldActionsProvider } from "../../../RiskAssessments/hooks/RiskAssessmentFieldActionsContext";
import { RiskFactorsSection } from "../../../RiskAssessments/components/RiskFactorsSection";
import { ActionsTakenSection } from "../../../RiskAssessments/components/ActionsTakenSection";
import { MAX_CHARS } from "../../IndividualSupportSession.constants";

export interface RiskAssessmentBlockProps {
  basePath: string;
  riskAssessment: IncidentReportIndividualSupportSessionRiskAssessment;
  patchRiskAssessment: (partial: Partial<IncidentReportIndividualSupportSessionRiskAssessment>) => void;
  isReadonly: boolean;
}

// AU-only (TPH-1907/1908) — reuses RiskFactorsSection/ActionsTakenSection from the standalone
// RiskAssessments module (see useRiskAssessmentFieldActions), scoped to this participant's
// nested `riskAssessment` object instead of a top-level `riskAssessment.participants[i]`.
export const RiskAssessmentBlock: FunctionComponent<RiskAssessmentBlockProps> = ({
  basePath,
  riskAssessment,
  patchRiskAssessment,
  isReadonly,
}) => {
  const { t } = useTranslation("criticalIncident");
  const { setFieldValue } = useFormikContext();
  const fieldActions = useRiskAssessmentFieldActions(basePath, riskAssessment, patchRiskAssessment, isReadonly);
  const { recordTouched, recordErrors } = fieldActions;

  const updateSupervisionField = (field: keyof IncidentReportIndividualSupportSessionRiskAssessment, value: unknown) => {
    setFieldValue(`${basePath}.${field}`, value);
    patchRiskAssessment({ [field]: value } as Partial<IncidentReportIndividualSupportSessionRiskAssessment>);
  };

  const handleSupervisionDateChange = (value: Moment | null) => {
    updateSupervisionField("supervisionDate", value?.startOf("day")?.toDate().toISOString() || null);
  };

  const showsSupervisionDetails = riskAssessment.wasThereSupervisionOrConsultation === "Y" || riskAssessment.wasThereSupervisionOrConsultation === "O";

  return (
    <Grid item sm={12}>
      <Grid container spacing={2}>
        {!isReadonly && (
          <Grid item sm={12}>
            <PriorityAlert priority="normal">
              {t("consultation_should_occur")}
            </PriorityAlert>
          </Grid>
        )}
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            type="radio.yesnoother"
            label={`${t("was_there_supervision_or_consultation")}*`}
            name={`${basePath}.wasThereSupervisionOrConsultation`}
            fullWidth
            error={Boolean(recordTouched?.wasThereSupervisionOrConsultation) && Boolean(recordErrors?.wasThereSupervisionOrConsultation)}
            helperText={recordTouched?.wasThereSupervisionOrConsultation ? t(recordErrors?.wasThereSupervisionOrConsultation || " ") : ""}
          >
            <ChooseYesNoOther
              label={t("was_there_supervision_or_consultation")}
              name={`${basePath}.wasThereSupervisionOrConsultation`}
              value={riskAssessment.wasThereSupervisionOrConsultation}
              onChange={(value: string) => updateSupervisionField("wasThereSupervisionOrConsultation", value)}
              required
            />
          </FormControlWrapper>
        </Grid>
        {showsSupervisionDetails && (
          <Grid item sm={12}>
            <FormControlWrapper
              isReadonly={isReadonly}
              label={t("supervision_or_consultation_other_reason")}
              name={`${basePath}.wasThereSupervisionOrConsultationOtherReason`}
              fullWidth
            >
              <TextBox
                multiline
                minRows={4}
                maxRows={6}
                label={t("supervision_or_consultation_other_reason")}
                name={`${basePath}.wasThereSupervisionOrConsultationOtherReason`}
                value={riskAssessment.wasThereSupervisionOrConsultationOtherReason}
                onChange={(event) => updateSupervisionField("wasThereSupervisionOrConsultationOtherReason", event.target.value)}
                slotProps={{ input: { maxLength: MAX_CHARS } }}
                required
                error={Boolean(recordTouched?.wasThereSupervisionOrConsultationOtherReason) && Boolean(recordErrors?.wasThereSupervisionOrConsultationOtherReason)}
                helperText={recordTouched?.wasThereSupervisionOrConsultationOtherReason ? t(recordErrors?.wasThereSupervisionOrConsultationOtherReason || " ") : ""}
              />
            </FormControlWrapper>
          </Grid>
        )}
        {showsSupervisionDetails && (
          <>
            <Grid item sm={12}>
              <FormControlWrapper
                isReadonly={isReadonly}
                type="date"
                dateFormat="L"
                label={t("supervision_date")}
                name={`${basePath}.supervisionDate`}
                required={riskAssessment.wasThereSupervisionOrConsultation === "Y"}
              >
                <DateInput
                  label={t("supervision_date")}
                  name={`${basePath}.supervisionDate`}
                  onChange={handleSupervisionDateChange}
                  value={riskAssessment.supervisionDate ? moment(riskAssessment.supervisionDate) : null}
                  error={Boolean(recordTouched?.supervisionDate) && Boolean(recordErrors?.supervisionDate)}
                  helperText={recordTouched?.supervisionDate ? t(recordErrors?.supervisionDate || " ") : ""}
                  fullWidth
                />
              </FormControlWrapper>
            </Grid>
            <Grid item sm={12}>
              <FormControlWrapper
                isReadonly={isReadonly}
                label={t("supervision_or_consultation_outcome")}
                name={`${basePath}.supervisionOrConsultationOutcome`}
                fullWidth
              >
                <TextBox
                  multiline
                  minRows={4}
                  maxRows={6}
                  label={t("supervision_or_consultation_outcome")}
                  name={`${basePath}.supervisionOrConsultationOutcome`}
                  value={riskAssessment.supervisionOrConsultationOutcome}
                  onChange={(event) => updateSupervisionField("supervisionOrConsultationOutcome", event.target.value)}
                  slotProps={{ input: { maxLength: MAX_CHARS } }}
                  required={riskAssessment.wasThereSupervisionOrConsultation === "Y"}
                  error={Boolean(recordTouched?.supervisionOrConsultationOutcome) && Boolean(recordErrors?.supervisionOrConsultationOutcome)}
                  helperText={recordTouched?.supervisionOrConsultationOutcome ? t(recordErrors?.supervisionOrConsultationOutcome || " ") : ""}
                />
              </FormControlWrapper>
            </Grid>
          </>
        )}

        <RiskAssessmentFieldActionsProvider value={fieldActions}>
          <RiskFactorsSection />
          <ActionsTakenSection />
        </RiskAssessmentFieldActionsProvider>
      </Grid>
    </Grid>
  );
};
